import React from 'react'
import {FaStar, FaPlay, FaHockeyPuck} from 'react-icons/fa'
import ListOfFins from "./ListofFins.json"
import ListOfSwedes from "./ListOfSwedes.json"
import { useContext, useState, useEffect } from 'react';
import { Link } from 'react-router-dom'
import NhlGamesContext from './context/Context';

// ─── Teletext helpers ────────────────────────────────────────────────────────

/**
 * Builds a flat list of display "lines" for one team's goals.
 * Each scorer entry is followed immediately by a Finnish-assist entry
 * if any assists were by Finnish players.
 *
 * @param {Array}  goals   – scoringPlays filtered to one team
 * @param {Array}  finnIds – array of Finnish player IDs
 * @returns {Array} of line objects
 */
function buildTeamLines(goals, finnIds) {
    const lines = [];
    goals.forEach(goal => {
        const scorer = goal.players[0].player;
        const isOT   = goal.about.ordinalNum === 'OT';
        const isSO   = goal.about.ordinalNum === 'SO';
        const isFinnishScorer = finnIds.includes(scorer.id);

        // Total elapsed game minutes
        const period   = goal.about.period;
        const rawTime  = parseFloat(goal.about.periodTime.replace(':', '.'));
        const elapsed  = rawTime + (period > 1 ? (period - 1) * 20 : 0);
        const timeDisplay = Math.floor(elapsed);

        // Last name only (works for "N. MacKinnon" → "MacKinnon")
        const lastName = scorer.fullName.includes('. ')
            ? scorer.fullName.split('. ').pop()
            : scorer.fullName.split(' ').pop();

        lines.push({
            type: 'scorer',
            name: lastName,
            time: timeDisplay,
            isOT,
            isSO,
            isFinnishScorer,
            discreteClip: goal.discreteClip
        });

        // Finnish assists — only shown when assisting player is Finnish
        const finnAssists = goal.players
            .filter(p => p.playerType === 'Assist' && finnIds.includes(p.player.id));

        if (finnAssists.length > 0) {
            const assistNames = finnAssists.map(a => {
                const fn = a.player.fullName;
                return fn.includes('. ') ? fn.split('. ').pop() : fn.split(' ').pop();
            });
            lines.push({
                type: 'assist',
                name: assistNames.join(', ')
            });
        }
    });
    return lines;
}

// A single cell in the teletext two-column grid
function TeletextLine({ line, onPlay }) {
    if (!line) return <div className="tt-cell tt-empty">&nbsp;</div>;

    if (line.type === 'assist') {
        return (
            <div className="tt-cell tt-assist">
                ({line.name})
            </div>
        );
    }

    // Scorer
    const colorClass = (line.isOT || line.isSO)
        ? 'tt-ot'
        : line.isFinnishScorer
            ? 'tt-finn'
            : 'tt-regular';

    return (
        <div
            className={`tt-cell ${colorClass}`}
            onClick={() => onPlay && onPlay(line.discreteClip)}
            title="Click to watch goal"
        >
            {line.name}
            <span className="tt-time">{line.time}</span>
        </div>
    );
}

// The full teletext goals section for one game
function TeletextGoals({ homeGoals, awayGoals, finnIds, onPlay }) {
    const homeLines = buildTeamLines(homeGoals, finnIds);
    const awayLines = buildTeamLines(awayGoals, finnIds);

    const maxLines = Math.max(homeLines.length, awayLines.length);
    if (maxLines === 0) return null;

    return (
        <div className="teletext-goals-section">
            <div className="teletext-goals-grid">
                {Array.from({ length: maxLines }).map((_, i) => (
                    <React.Fragment key={i}>
                        <TeletextLine line={homeLines[i]} onPlay={onPlay} />
                        <TeletextLine line={awayLines[i]} onPlay={onPlay} />
                    </React.Fragment>
                ))}
            </div>
        </div>
    );
}

// ─── Main component ───────────────────────────────────────────────────────────

function SingleGame({games}) {
    const {toggle, wantfins, wantswedes, favorites, startPlay, ToFavs, theme} = useContext(NhlGamesContext)
    const [gamesWithScoring, setGamesWithScoring] = useState([]);

    const wanted_fin = wantfins ? ListOfFins : []
    const wanted_swe = wantswedes ? ListOfSwedes : []
    const wantedIds = [...wanted_fin, ...wanted_swe]

    const isTeletext = theme === '235';

    // Transform goals data from /score endpoint (no additional API calls needed!)
    useEffect(() => {
        const transformGames = () => {
            const gamesWithData = games.map(game => {
                // Transform goals array to scoringPlays format
                const scoringPlays = [];
                let prevHomeScore = 0;

                if (game.goals && Array.isArray(game.goals)) {
                    game.goals.forEach((goal) => {
                        // Determine which team scored by tracking score increments
                        const isHome = goal.homeScore > prevHomeScore;
                        prevHomeScore = goal.homeScore;

                        scoringPlays.push({
                            about: {
                                eventId: goal.discreteClip, // Use discreteClip as eventId
                                period: goal.period,
                                periodTime: goal.timeInPeriod,
                                ordinalNum: goal.periodDescriptor.number === 4 ? 'OT' :
                                           (goal.periodDescriptor.periodType === 'SO' ? 'SO' :
                                            goal.periodDescriptor.number.toString()),
                                goals: {
                                    away: goal.awayScore,
                                    home: goal.homeScore
                                }
                            },
                            players: [
                                {
                                    player: {
                                        id: goal.playerId,
                                        fullName: goal.name.default
                                    },
                                    playerType: 'Scorer'
                                },
                                ...(goal.assists || []).map(assist => ({
                                    player: {
                                        id: assist.playerId,
                                        fullName: assist.name ? assist.name.default : 'Unknown'
                                    },
                                    playerType: 'Assist'
                                }))
                            ],
                            discreteClip: goal.discreteClip, // Add discreteClip for video playback
                            isHome // Track which team scored for teletext layout
                        });
                    });
                }
                return { ...game, scoringPlays };
            });

            setGamesWithScoring(gamesWithData);
        };

        if (games.length > 0) {
            transformGames();
        }
    }, [games, toggle]);

    const handleRecapPlay = async (game, isCondensed) => {
      // Extract video ID from path (e.g., "/video/chi-at-bos-recap-6382535026112" -> "6382535026112")
      const videoPath = isCondensed ? game.condensedGame : game.threeMinRecap;
      if (!videoPath) return;

      const videoId = videoPath.match(/(\d+)$/)?.[1];
      if (!videoId) return;

      const videoUrl = await fetchBrightcoveVideo(videoId);
      if (videoUrl == null) return;
      startPlay(videoUrl);
    };

    const PlaySingleGoal = async (discreteClip) => {
      if (!discreteClip) return;
      const video_url = await fetchBrightcoveVideo(discreteClip);
      if (video_url == null) return;
      startPlay(video_url);
    };


    function SingleScore({toggle, score, winning}) {
        return (toggle) ? (
        <p className={`team-score ${winning ? "winning" : ""}`}>{score}</p>
        ): ''
      }

    function SingleGoal({scoringplay}) {
          const playergoals = scoringplay.players.filter(player => !player.playerType.includes("Goalie")).map(players => players.player)

          const goal_time = parseFloat(scoringplay.about.periodTime.replace(':','.')) + (scoringplay.about.period > 1 ? (scoringplay.about.period-1)*20 : null)
          const PlayerIDs = playergoals.map(pid => pid.id)
          const score = scoringplay.about.goals.home + ' - ' + scoringplay.about.goals.away
          const IsFinn = wantedIds.some( ai => PlayerIDs.includes(ai) )
          return <><div className={`goal ${IsFinn ? "Highlight" : ""}`}>
            <span className='goal-time'>{scoringplay.about.ordinalNum === 'SO' ? 'SO' : parseFloat(goal_time).toFixed(2)}</span>
            <FaHockeyPuck className='react-icons'/> {playergoals[0].fullName} {playergoals.length > 1 && '('+playergoals.splice(1,2).map(p => p.fullName).join(", ")+')'}
            <span className='curr-score'>{scoringplay.about.ordinalNum !== 'SO' ? score : null}</span> </div>
            <div className='vid_link'>{scoringplay.about.ordinalNum !== 'SO' && <FaPlay className='react-icons' onClick={() => PlaySingleGoal(scoringplay.discreteClip)} />}</div>
            </>
    }


    const displayGames = gamesWithScoring.length > 0 ? gamesWithScoring : games;

    return (
        displayGames.map((game) => {

          const isRecap = game.gameState === "OFF" || game.gameState === "FINAL"
          const timestamp = new Date(game.startTimeUTC || game.gameDate)

          // ── Teletext 235 rendering ──────────────────────────────────────────
          if (isTeletext) {
            const scoringPlays = game.scoringPlays || [];
            const homeGoals = scoringPlays.filter(sp => sp.isHome);
            const awayGoals = scoringPlays.filter(sp => !sp.isHome);

            // Determine OT / SO
            const hasOT = scoringPlays.some(sp => sp.about.ordinalNum === 'OT');
            const hasSO = scoringPlays.some(sp => sp.about.ordinalNum === 'SO');
            const extraTime = hasSO ? 'SO' : hasOT ? 'OT' : '';

            const homeScore = game.homeTeam?.score;
            const awayScore = game.awayTeam?.score;
            const gameStarted = game.gameState !== "FUT" && game.gameState !== "PRE";

            const homeName = game.homeTeam?.placeName?.default || game.homeTeam?.abbrev || '';
            const awayName = game.awayTeam?.placeName?.default || game.awayTeam?.abbrev || '';

            return (
              <div className="singlegame teletext-game-card" key={game.id}>

                {/* Blue header bar: TeamA  -  TeamB  [OT] score */}
                <div className="teletext-game-header">
                  <span className="tt-team-name">{homeName}</span>
                  <span className="tt-separator"> – </span>
                  <span className="tt-team-name">{awayName}</span>
                  {gameStarted ? (
                    toggle && <span className="tt-score-result">
                      {extraTime && <span className="ot-indicator">{extraTime}</span>}
                      {homeScore}-{awayScore}
                    </span>
                  ) : (
                    <span className="tt-starttime">
                      {timestamp.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  )}
                </div>

                {/* Two-column goal list */}
                {toggle && gameStarted && (
                  <TeletextGoals
                    homeGoals={homeGoals}
                    awayGoals={awayGoals}
                    finnIds={ListOfFins}
                    onPlay={PlaySingleGoal}
                  />
                )}

                {/* Recap links at the bottom — always show when available, regardless of score toggle */}
                {isRecap && (
                  <div className="teletext-recaps">
                    {game.condensedGame && (
                      <button className="tt-recap-btn" onClick={() => handleRecapPlay(game, true)}>
                        ▶ Extended
                      </button>
                    )}
                    {game.threeMinRecap && (
                      <button className="tt-recap-btn" onClick={() => handleRecapPlay(game, false)}>
                        ▶ Recap
                      </button>
                    )}
                  </div>
                )}

              </div>
            );
          }

          // ── Default (non-teletext) rendering ────────────────────────────────
          return (
          <div
          className={`singlegame ${!toggle ? "minified" : ""}`} key={game.id}>
            <div className="home">

            <span className='fav'><FaStar onClick={() => ToFavs(game.homeTeam.id)} className={`${favorites.includes(game.homeTeam.id.toString()) ? "checked react-icons" : "react-icons"}`}/></span>
                <Link to={'/teams/'+game.homeTeam.id} >
              <img className='team-image' src={game.homeTeam.darkLogo || "https://assets.nhle.com/logos/nhl/svg/"+game.homeTeam.abbrev+"_dark.svg"} alt={game.homeTeam.commonName?.default || game.homeTeam.placeName?.default} />
              </Link>
              <Link style={{ textDecoration: 'none', color: '#c9d1d9' }}to={'/teams/'+game.homeTeam.id} ><p className="team-name">{game.homeTeam.placeName?.default} {game.homeTeam.commonName?.default}</p></Link>
              {game.gameState !== "FUT" && game.gameState !== "PRE" ?
              <SingleScore toggle={toggle} score={game.homeTeam.score}  winning={(game.homeTeam.score > game.awayTeam.score) ? true : false} />:null}

            </div>
            <div className="away">
            <span className='fav'><FaStar onClick={() => ToFavs(game.awayTeam.id)} className={`${favorites.includes(game.awayTeam.id.toString()) ? "checked react-icons" : "react-icons"}`} /></span>
            <Link to={'/teams/'+game.awayTeam.id} >
              <img className='team-image' src={game.awayTeam.darkLogo || "https://assets.nhle.com/logos/nhl/svg/"+game.awayTeam.abbrev+"_dark.svg"} alt={game.awayTeam.commonName?.default || game.awayTeam.placeName?.default} />
              </Link>
              <Link style={{ textDecoration: 'none', color: '#c9d1d9' }}to={'/teams/'+game.awayTeam.id} ><p className="team-name">{game.awayTeam.placeName?.default} {game.awayTeam.commonName?.default}</p></Link>
              {game.gameState !== "FUT" && game.gameState !== "PRE" ?
              <SingleScore toggle={toggle} score={game.awayTeam.score} winning={(game.homeTeam.score < game.awayTeam.score) ? true : false}/> : <p className='starttime'>Starts at {timestamp.toLocaleTimeString('it-IT',{ hour: '2-digit', minute: '2-digit' })}</p>}
            </div>
            {isRecap && <>
            <div className='recaps'>
            <span className='recap'>Extended Highlights</span> <span className='recap_link'><FaPlay className='react-icons' onClick={() => handleRecapPlay(game, true)} /></span>
            <span className='recap'>Recap</span><span className='recap_link'><FaPlay className='react-icons' onClick={() => handleRecapPlay(game, false)}  /></span></div></>}



            <div className='goals'>
            {toggle && game.scoringPlays && game.scoringPlays.map(scoringplay => <SingleGoal scoringplay={scoringplay} key={scoringplay.about.eventId}/>)}
            </div>
          </div>
        )})
      )
}

// Unified function to fetch Brightcove video URL from video ID
const fetchBrightcoveVideo = async (videoId) => {
  try {
    const response = await fetch(`https://hehe.nhlrecap.com/?https://edge.api.brightcove.com/playback/v1/accounts/6415718365001/videos/${videoId}`, {
      headers: {
        'accept': 'application/json;pk=BCpkADawqM3l37Vq8trLJ95vVwxubXYZXYglAopEZXQTHTWX3YdalyF9xmkuknxjBgiMYwt8VZ_OZ1jAjYxz_yzuNh_cjC3uOaMspVTD-hZfNUHtNnBnhVD0Gmsih8TBF8QlQFXiCQM3W_u4ydJ1qK2Rx8ZutCUg3PHb7Q',
        'x-cors-api-key': 'temp_ec89abfc07d1cd3d760c3e4397975d32'
      }
    });

    if (!response.ok) {
      throw new Error('Network response was not ok');
    }

    const data = await response.json();

    // Get sources[1].src for HTTPS video URL
    if (data.sources && data.sources.length > 1 && data.sources[1].src) {
      return data.sources[1].src;
    } else {
      console.error('No video sources found in Brightcove response');
      return null;
    }
  } catch (error) {
    console.error('Error fetching Brightcove video:', error);
    return null;
  }
};

export default SingleGame
